import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Banknote,
  BookOpen,
  Brain,
  Briefcase,
  Calculator,
  Calendar,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  FolderOpen,
  GraduationCap,
  Layers,
  Search,
  Users,
  X
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { api } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
// Les modales sont portees dans document.body : rendues en place, elles restaient
// prisonnieres du contexte d'empilement de la page et passaient sous l'en-tete
// fixe du layout, malgre un z-index bien superieur.
import { MODAL_OVERLAY_Z40_CLASS, MODAL_OVERLAY_Z50_CLASS } from './ui/modalOverlay';
import { Toast } from '../components/ui/Toast';
import EmployeePaimentHistory from './EmployeePaimentHistory';
import { SalaryModificationHistoryModal, SalaryModificationRequestModal } from './SalaryModificationModals';
import NoticePaiementCourte from './paie/NoticePaiementCourte';

const toNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) => Math.round(toNumber(value)).toLocaleString('fr-FR') + ' FCFA';

const normalizeText = (value = '') => value
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const getComposantes = (hybride) => (Array.isArray(hybride?.composantes) ? hybride.composantes : []);

const getHybrideName = (hybride) => {
  if (!hybride) return '';
  return hybride.nom_complet || ((hybride.prenom || '') + ' ' + (hybride.nom || '')).trim() || 'Employe hybride';
};

const getComposanteLabel = (composante) => {
  const role = composante.role || composante.role_nom || composante.categorie || 'Role';
  const mode = composante.mode_remuneration || composante.type_contrat || '';
  return mode ? role + ' - ' + mode : role;
};

const isHourlyComposante = (composante) => String(composante?.mode_remuneration || composante?.type_contrat || '').toUpperCase() === 'HORAIRE';

const getFixedComposantes = (hybride) => getComposantes(hybride).filter((composante) => !isHourlyComposante(composante));

const canModifyFixedComposante = (composante) => {
  if (!composante || isHourlyComposante(composante)) return false;
  const roleIds = Array.isArray(composante.employe_role_ids) ? composante.employe_role_ids.filter(Boolean) : [];
  return Boolean(composante.employe_role_id) && roleIds.length <= 1;
};

const uniqueByKey = (items = [], getKey) => {
  const seen = new Set();
  return items.filter((item, index) => {
    const key = getKey(item, index);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getHybrideChildrenInfo = (hybride) => {
  const details = getComposantes(hybride)
    .map((composante) => composante.retenues_enfants_detail || {})
    .filter(Boolean);

  const localTotal = details.reduce((sum, detail) => sum + toNumber(detail.local?.total), 0);
  const intersiteTotal = details.reduce((sum, detail) => sum + toNumber(detail.intersite?.total), 0);
  const localCount = Math.max(0, ...details
    .filter((detail) => toNumber(detail.local?.total) > 0 || toNumber(detail.local?.total_global) > 0)
    .map((detail) => Number(detail.local?.nombre_enfants || 0)));
  const intersiteCount = Math.max(0, ...details
    .filter((detail) => toNumber(detail.intersite?.total) > 0 || toNumber(detail.intersite?.total_global) > 0)
    .map((detail) => Number(detail.intersite?.nombre_enfants || 0)));

  const localDetails = uniqueByKey(
    details.flatMap((detail) => detail.local?.detail_enfants || []),
    (item, index) => item.eleve_id || item.id || item.matricule || item.nom_complet || index
  );
  const intersiteDetails = uniqueByKey(
    details.flatMap((detail) => detail.intersite?.detail_enfants || detail.intersite?.enfants_intersite || []),
    (item, index) => item.link_id || item.eleve_id || item.id || item.nom_complet || item.nom || index
  );

  return {
    total: localTotal + intersiteTotal,
    local_total: localTotal,
    intersite_total: intersiteTotal,
    nombre_local: localCount,
    nombre_intersite: intersiteCount,
    nombre_total: localCount + intersiteCount,
    detail_local: localDetails,
    detail_intersite: intersiteDetails,
    repartition: getComposantes(hybride)
      .map((composante) => ({
        ligne_key: composante.ligne_key,
        role: getComposanteLabel(composante),
        type_personnel: composante.type_personnel,
        local: toNumber(composante.retenues_enfants_detail?.local?.total),
        intersite: toNumber(composante.retenues_enfants_detail?.intersite?.total)
      }))
      .filter((item) => item.local > 0 || item.intersite > 0)
  };
};

const getPointageLabel = (composante) => {
  const pointages = composante.pointages || {};
  const totalHeures = toNumber(composante.total_heures ?? pointages.total_heures);
  const nombreCours = toNumber(composante.nombre_cours ?? pointages.nombre);
  const joursPointes = toNumber(composante.jours_pointes ?? pointages.jours_pointes);

  if (totalHeures > 0) return totalHeures.toLocaleString('fr-FR') + ' h';
  if (nombreCours > 0) return nombreCours + ' cours';
  if (joursPointes > 0) return joursPointes + ' jours';
  return '0';
};

const ComptabiliteHybrides = ({
  selectedDate,
  setSelectedDate,
  showCalendar,
  setShowCalendar,
  modesPaiement,
  loadModesPaiement,
  anneeActive,
  showToast,
  formatDateForDisplay,
  goToPreviousMonth,
  goToNextMonth,
  onBackToDashboard,
  salaryAuthorization
}) => {
  const [hybrides, setHybrides] = useState([]);
  const [selectedHybride, setSelectedHybride] = useState('');
  const [loadingHybrides, setLoadingHybrides] = useState(false);
  const [searchTermHybride, setSearchTermHybride] = useState('');
  const [paiementExistantHybride, setPaiementExistantHybride] = useState(null);
  const [calculationValidatedHybride, setCalculationValidatedHybride] = useState(false);
  const [paymentValidatedHybride, setPaymentValidatedHybride] = useState(false);
  const [showValidationDialogHybride, setShowValidationDialogHybride] = useState(false);
  const [isValidatingHybride, setIsValidatingHybride] = useState(false);
  const [showPaymentDialogHybride, setShowPaymentDialogHybride] = useState(false);
  const [isProcessingPaymentHybride, setIsProcessingPaymentHybride] = useState(false);
  const [selectedModePaiementHybride, setSelectedModePaiementHybride] = useState('');
  const [showHistoriqueHybrides, setShowHistoriqueHybrides] = useState(false);
  const [salaryRequestEmploye, setSalaryRequestEmploye] = useState(null);
  const [salaryHistoryEmploye, setSalaryHistoryEmploye] = useState(null);
  const [showRetenueModal, setShowRetenueModal] = useState(false);
  const [selectedRetenueTarget, setSelectedRetenueTarget] = useState(null);
  const [retenueAbsence, setRetenueAbsence] = useState('');
  const [typeRetenue, setTypeRetenue] = useState('avec');
  const [justificationPasDeRetenue, setJustificationPasDeRetenue] = useState('');
  const [showModalEnfantsCharge, setShowModalEnfantsCharge] = useState(false);
  const [selectedHybrideEnfantsDetail, setSelectedHybrideEnfantsDetail] = useState(null);
  const [isModePaiementDropdownOpen, setIsModePaiementDropdownOpen] = useState(false);
  const [modePaiementSearchTerm, setModePaiementSearchTerm] = useState('');
  const [paymentStep, setPaymentStep] = useState('payment');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  const selectedHybrideData = useMemo(
    () => hybrides.find((hybride) => String(hybride.employe_id) === String(selectedHybride)) || null,
    [hybrides, selectedHybride]
  );

  const salaryPaymentLocked = salaryAuthorization?.paiement_autorise === false;
  const salaryLockReasons = salaryAuthorization?.raisons_blocage || [];
  const getSalaryLockMessage = () => salaryLockReasons[0] || "Paiement des salaires verrouillé. Validez l'état des salaires et la dotation centrale avant paiement.";

  const SalaryLockPanel = () => salaryPaymentLocked ? (
    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          <p className="font-semibold">Paiement verrouille</p>
          <p className="text-xs">{getSalaryLockMessage()}</p>
        </div>
      </div>
    </div>
  ) : null;

  useEffect(() => {
    loadHybridesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, anneeActive?.id]);

  useEffect(() => {
    loadModesPaiement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [toast]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isModePaiementDropdownOpen && !event.target.closest('.mode-paiement-dropdown')) {
        setIsModePaiementDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isModePaiementDropdownOpen]);

  const localShowToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    if (showToast && message && message.length > 120) showToast(message, type);
  };

  const buildSalaryModalEmployee = (hybride, composante) => ({
    id: hybride.employe_id,
    nom: hybride.nom,
    prenom: hybride.prenom,
    nom_complet: getHybrideName(hybride),
    employe_role_id: composante?.employe_role_id,
    role: getComposanteLabel(composante || {}),
    role_nom: getComposanteLabel(composante || {}),
    salaire_fixe: toNumber(composante?.salaire_brut),
    salaire_base: toNumber(composante?.salaire_brut),
    type_contrat: composante?.mode_remuneration || 'MENSUEL'
  });

  const handleOpenSalaryRequestHybride = (hybride, composante = null) => {
    const fixedComposantes = composante ? [composante] : getFixedComposantes(hybride);
    if (!fixedComposantes.length) {
      localShowToast('Aucune composante fixe disponible pour cet employe hybride. La partie horaire se calcule depuis les heures travaillees.', 'error');
      return;
    }
    if (!composante && fixedComposantes.length > 1) {
      localShowToast('Selectionnez la composante fixe a modifier dans le tableau des composantes.', 'error');
      return;
    }
    if (!canModifyFixedComposante(fixedComposantes[0])) {
      localShowToast('Cette composante fixe regroupe plusieurs roles ou ne fournit pas de role cible. La modification doit etre detaillee par role.', 'error');
      return;
    }
    setSalaryRequestEmploye(buildSalaryModalEmployee(hybride, fixedComposantes[0]));
  };

  const handleOpenSalaryHistoryHybride = (hybride) => {
    setSalaryHistoryEmploye({
      id: hybride.employe_id,
      nom: hybride.nom,
      prenom: hybride.prenom,
      nom_complet: getHybrideName(hybride)
    });
  };

  const handleOpenRetenueHybride = (hybride) => {
    setSelectedRetenueTarget({
      employe_id: hybride.employe_id,
      nom: getHybrideName(hybride),
      type: 'hybride'
    });
    setRetenueAbsence(hybride.retenue_temp ? String(hybride.retenue_temp) : '');
    setTypeRetenue(hybride.retenue_temp !== undefined && hybride.retenue_temp !== null ? 'avec' : 'avec');
    setJustificationPasDeRetenue(hybride.justification_retenue_temp || '');
    setShowRetenueModal(true);
  };

  const handleSaveRetenueHybride = () => {
    if (typeRetenue === 'avec' && (!retenueAbsence || Number(retenueAbsence) <= 0)) {
      localShowToast('Veuillez saisir le montant de la retenue', 'error');
      return;
    }

    if (!justificationPasDeRetenue.trim()) {
      localShowToast('Veuillez saisir une justification', 'error');
      return;
    }

    const montant = typeRetenue === 'avec' ? Math.round(Number(retenueAbsence || 0)) : 0;
    const justification = justificationPasDeRetenue.trim();

    setHybrides((previous) => previous.map((hybride) => {
      if (String(hybride.employe_id) !== String(selectedRetenueTarget?.employe_id)) return hybride;
      return {
        ...hybride,
        retenue_temp: montant,
        justification_retenue_temp: justification
      };
    }));

    setCalculationValidatedHybride(false);
    setShowRetenueModal(false);
    localShowToast(
      montant > 0
        ? 'Retenue hybride enregistree. Elle sera deduite lors du paiement.'
        : 'Absence de retenue manuelle enregistree avec justification.',
      'success'
    );
  };

  const syncSelectionState = (hybride) => {
    if (hybride?.paiement_existe && hybride?.paiement_info) {
      setPaiementExistantHybride(hybride.paiement_info);
      setPaymentValidatedHybride(true);
      setCalculationValidatedHybride(true);
      return;
    }

    if (hybride?.paiement_existe) {
      setPaiementExistantHybride({
        statut: 'cloture',
        montant_net: hybride.reste_a_payer || hybride.net_a_payer || 0,
        montant_brut: hybride.salaire_brut || 0,
        montant_retenues: hybride.retenues_total || 0,
        montant_avances: hybride.avances_total || 0
      });
      setPaymentValidatedHybride(true);
      setCalculationValidatedHybride(true);
      return;
    }

    setPaiementExistantHybride(null);
    setPaymentValidatedHybride(false);
    setCalculationValidatedHybride(false);
  };

  const loadHybridesData = async () => {
    setLoadingHybrides(true);
    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();
      const selectedYearId = anneeActive?.id || localStorage.getItem('annee_scolaire_id');
      const response = await api.paiementsSalaires.getHybridesAvecPointages(mois, annee, selectedYearId);

      if (!response.success) {
        localShowToast(response.message || response.error || 'Erreur lors du chargement des employes hybrides', 'error');
        setHybrides([]);
        setSelectedHybride('');
        syncSelectionState(null);
        return;
      }

      const lignes = Array.isArray(response.data) ? response.data : [];
      setHybrides(lignes);

      if (lignes.length === 0) {
        setSelectedHybride('');
        syncSelectionState(null);
        return;
      }

      const selectedLine = lignes.find((line) => String(line.employe_id) === String(selectedHybride)) || lignes[0];
      setSelectedHybride(String(selectedLine.employe_id));
      syncSelectionState(selectedLine);
    } catch (error) {
      console.error('Erreur chargement hybrides:', error);
      localShowToast(error.message || 'Erreur lors du chargement des employes hybrides', 'error');
    } finally {
      setLoadingHybrides(false);
    }
  };

  const calculerRemunerationHybride = (hybride) => {
    if (!hybride) {
      return {
        salaire_base: 0,
        salaire_net: 0,
        net_a_payer: 0,
        total_retenues: 0,
        total_avances: 0,
        nombre_composantes: 0,
        nombre_cours: 0,
        total_heures: 0,
        jours_pointes: 0,
        jours_manquants: 0,
        composantes: [],
        peut_payer: false,
        message_erreur: 'Aucun employe hybride selectionne'
      };
    }

    const composantes = getComposantes(hybride);
    const sumComposantes = (field) => composantes.reduce((sum, composante) => sum + toNumber(composante[field]), 0);
    const childrenInfo = getHybrideChildrenInfo(hybride);
    const salaireBase = toNumber(hybride.salaire_brut || sumComposantes('salaire_brut'));
    const retenuesPresence = toNumber(hybride.retenues_presence_total || hybride.retenues_presence || sumComposantes('retenues_presence'));
    const retenuesEnfants = toNumber(hybride.retenues_enfants_charge || hybride.retenues_enfants || childrenInfo.total || sumComposantes('retenues_enfants'));
    const retenueGlobale = toNumber(hybride.retenue_temp);
    const retenuesTotal = retenuesPresence + retenuesEnfants + retenueGlobale;
    const avancesTotal = toNumber(hybride.avances_total || sumComposantes('avances_total'));
    const netSansRetenueManuelle = toNumber(hybride.net_a_payer || sumComposantes('net_a_payer'));
    const resteSansRetenueManuelle = toNumber(hybride.reste_a_payer || netSansRetenueManuelle);
    const netAPayer = Math.max(0, netSansRetenueManuelle - retenueGlobale);
    const resteAPayer = Math.max(0, resteSansRetenueManuelle - retenueGlobale);
    const alertes = Array.isArray(hybride.alertes) ? hybride.alertes : [];

    const nombreCours = composantes.reduce((sum, composante) => sum + toNumber(composante.nombre_cours ?? composante.pointages?.nombre), 0);
    const totalHeures = composantes.reduce((sum, composante) => sum + toNumber(composante.total_heures ?? composante.pointages?.total_heures), 0);
    const joursPointes = composantes.reduce((sum, composante) => sum + toNumber(composante.jours_pointes ?? composante.pointages?.jours_pointes ?? composante.pointages?.nombre), 0);
    const joursManquants = composantes.reduce((sum, composante) => sum + toNumber(composante.jours_manquants ?? composante.pointages?.jours_manquants), 0);

    return {
      salaire_base: salaireBase,
      salaire_net: resteAPayer,
      net_a_payer: netAPayer,
      total_retenues: retenuesTotal,
      total_retenues_presences: retenuesPresence,
      retenue_globale: retenueGlobale,
      justification_retenue_temp: hybride.justification_retenue_temp || '',
      retenues_enfants_charge: retenuesEnfants,
      retenues_enfants_locaux: childrenInfo.local_total,
      retenues_enfants_intersite: childrenInfo.intersite_total,
      nombre_enfants_charge: childrenInfo.nombre_total,
      nombre_enfants_locaux: childrenInfo.nombre_local,
      nombre_enfants_intersite: childrenInfo.nombre_intersite,
      detail_enfants_charge: childrenInfo.detail_local,
      detail_enfants_intersite: childrenInfo.detail_intersite,
      detail_repartition_enfants: childrenInfo.repartition,
      total_avances: avancesTotal,
      nombre_composantes: composantes.length,
      nombre_cours: nombreCours,
      total_heures: totalHeures,
      jours_pointes: joursPointes,
      jours_manquants: joursManquants,
      composantes,
      peut_payer: alertes.length === 0 && resteAPayer > 0 && !hybride.paiement_existe,
      message_erreur: alertes.join(' | ') || 'Le calcul hybride ne peut pas encore etre paye.'
    };
  };

  const handleSelectHybride = (hybride) => {
    setSelectedHybride(String(hybride.employe_id));
    syncSelectionState(hybride);
  };

  const handleValidateCalculationHybride = async () => {
    if (!selectedHybrideData) {
      localShowToast('Selectionnez un employe hybride', 'error');
      return;
    }

    const calcul = calculerRemunerationHybride(selectedHybrideData);
    if (!calcul.peut_payer) {
      localShowToast(calcul.message_erreur || 'Ce salaire hybride ne peut pas etre paye', 'error');
      return;
    }

    setIsValidatingHybride(true);
    setShowValidationDialogHybride(false);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setCalculationValidatedHybride(true);
    setIsValidatingHybride(false);
    localShowToast('Calcul hybride valide', 'success');
  };

  const handleOpenPaymentDialogHybride = () => {
    if (salaryPaymentLocked) {
      localShowToast(getSalaryLockMessage(), 'error');
      return;
    }
    if (!calculationValidatedHybride) {
      localShowToast('Validez le calcul hybride avant le paiement', 'error');
      return;
    }
    setSelectedModePaiementHybride('');
    setModePaiementSearchTerm('');
    setIsModePaiementDropdownOpen(false);
    setShowPaymentDialogHybride(true);
  };

  const handleProcessPaymentHybride = async () => {
    if (salaryPaymentLocked) {
      localShowToast(getSalaryLockMessage(), 'error');
      return;
    }
    if (!selectedModePaiementHybride) {
      localShowToast('Veuillez selectionner un mode de paiement', 'error');
      return;
    }

    const hybride = hybrides.find((line) => String(line.employe_id) === String(selectedHybride));
    const calcul = calculerRemunerationHybride(hybride);
    setShowPaymentDialogHybride(false);
    setIsProcessingPaymentHybride(true);
    setPaymentStep('payment');

    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();
      const selectedYearId = anneeActive?.id || localStorage.getItem('annee_scolaire_id');
      const response = await api.paiementsSalaires.creerPaiementHybride({
        employe_id: Number(selectedHybride),
        mois,
        annee,
        annee_scolaire_id: selectedYearId,
        mode_paiement_id: Number(selectedModePaiementHybride),
        montant_retenue_manuelle: calcul.retenue_globale || 0,
        justification_retenue_manuelle: calcul.justification_retenue_temp || ''
      });

      if (!response.success) {
        localShowToast(response.message || response.error || 'Erreur lors du paiement hybride', 'error');
        setIsProcessingPaymentHybride(false);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 8000));
      setPaymentStep('cloture');

      const paiements = response.data?.paiements || [];
      const clotureResponse = await api.paiementsSalaires.cloturerPaiementHybride(paiements);
      if (!clotureResponse.success) {
        localShowToast(clotureResponse.message || 'Paiement effectue mais echec de la cloture', 'warning');
        setPaymentValidatedHybride(true);
        setCalculationValidatedHybride(true);
        setIsProcessingPaymentHybride(false);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 5000));
      const avanceIds = Array.from(new Set(paiements.flatMap((paiement) => paiement.avances_ids || [])));
      await Promise.all(avanceIds.map(async (avanceId) => {
        try {
          await api.avancesSalaire.marquerAvanceAppliquee(avanceId);
        } catch (error) {
          console.warn('Avance hybride non marquee appliquee:', avanceId, error.message);
        }
      }));

      setPaiementExistantHybride({
        statut: 'cloture',
        date_paiement: new Date().toISOString(),
        date_cloture: new Date().toISOString(),
        montant_net: calcul.salaire_net,
        montant_brut: calcul.salaire_base,
        montant_retenues: calcul.total_retenues,
        montant_avances: calcul.total_avances
      });
      setPaymentValidatedHybride(true);
      setCalculationValidatedHybride(true);
      await loadHybridesData();
      localShowToast('Paiement hybride cloture avec succes', 'success');
    } catch (error) {
      console.error('Erreur paiement hybride:', error);
      localShowToast(error.message || 'Erreur lors du traitement du paiement hybride', 'error');
    } finally {
      setIsProcessingPaymentHybride(false);
      setPaymentStep('payment');
    }
  };

  const handleDownloadFichePaieHybride = async (paiementId, paiement) => {
    try {
      if (paiement?.type_salaire === 'enseignant') {
        await api.paiementsSalaires.genererFichePaieEnseignant(paiementId);
      } else {
        await api.paiementsSalaires.genererFichePaieEmploye(paiementId);
      }
      localShowToast('Fiche de paie telechargee avec succes !', 'success');
    } catch (error) {
      console.error('Erreur telechargement fiche hybride:', error);
      localShowToast('Erreur lors du telechargement', 'error');
    }
  };

  const filteredHybrides = hybrides.filter((hybride) => {
    const composantesText = getComposantes(hybride).map(getComposanteLabel).join(' ');
    return normalizeText(getHybrideName(hybride) + ' ' + composantesText).includes(normalizeText(searchTermHybride));
  });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6">
          <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mb-6">
            <div className="flex items-center mb-4">
              <button onClick={onBackToDashboard} className="flex items-center text-[#246990] hover:text-[#1f5a7a] mr-4">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour au dashboard
              </button>
            </div>

            <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5">
              <div className="p-2 bg-[#246990] rounded-full mr-4">
                <Layers className="text-white" size={20} />
              </div>
              <div className="flex-1">
                <h1 className="font-medium text-[#246990] text-xl">Employes hybrides</h1>
                <p className="text-sm text-gray-600">Gestion des salaires consolides fixe et horaire</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Periode selectionnee</div>
                <div className="font-medium text-gray-800 capitalize">{formatDateForDisplay(selectedDate)}</div>
                <div className="text-xs text-gray-500">Salaires hybrides</div>
              </div>
            </div>

            {/* Rappel avant paiement (version courte, voir SalaryManagementPage) */}
            <NoticePaiementCourte />

            <div className="flex items-center justify-end space-x-2 bg-gray-50 rounded-lg p-2">
              <button onClick={goToPreviousMonth} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="relative">
                <button onClick={() => setShowCalendar(!showCalendar)} className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">
                  <Calendar className="h-4 w-4 mr-2 text-[#246990]" />
                  <span className="text-sm font-medium text-gray-900 capitalize">{formatDateForDisplay(selectedDate)}</span>
                </button>
                {showCalendar && (
                  <div className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                    <input
                      type="month"
                      value={selectedDate.getFullYear() + '-' + String(selectedDate.getMonth() + 1).padStart(2, '0')}
                      onChange={(event) => {
                        const [year, month] = event.target.value.split('-');
                        setSelectedDate(new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1));
                        setShowCalendar(false);
                      }}
                      className="p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                )}
              </div>
              <button onClick={goToNextMonth} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={() => setShowHistoriqueHybrides(true)} className="px-3 py-2 text-xs bg-[#246990] text-white rounded-md hover:bg-[#246990]/90 transition-colors flex items-center">
                <FolderOpen className="w-3 h-3 mr-1" />
                Registre comptable
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm xl:col-span-2">
              <div className="p-3 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-4">
                <div className="p-2 bg-[#246990] rounded-full mr-2">
                  <Users className="text-white" size={14} />
                </div>
                <div className="flex-1">
                  <h2 className="font-medium text-[#246990] text-base">Hybrides</h2>
                  <p className="text-xs text-gray-600">Selection</p>
                </div>
              </div>

              <div className="mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                  <input type="text" placeholder="Rechercher..." value={searchTermHybride} onChange={(event) => setSearchTermHybride(event.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
                </div>
              </div>

              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {loadingHybrides ? (
                  <div className="text-center py-8 text-gray-400">
                    <div className="animate-spin w-8 h-8 border-4 border-[#246990] border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-sm mt-2">Chargement...</p>
                  </div>
                ) : filteredHybrides.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Layers className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucun employe hybride trouve</p>
                  </div>
                ) : filteredHybrides.map((hybride) => {
                  const active = selectedHybride === String(hybride.employe_id);
                  return (
                    <div key={hybride.ligne_key || hybride.employe_id} onClick={() => handleSelectHybride(hybride)} className={'p-2 rounded-lg border cursor-pointer transition-all ' + (active ? 'border-[#246990] bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50')}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className={'text-sm font-medium truncate ' + (active ? 'text-[#246990]' : 'text-gray-900')}>{getHybrideName(hybride)}</div>
                            {hybride.paiement_existe && <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full flex-shrink-0">Paye</span>}
                          </div>
                          <div className="text-xs text-gray-500">{getComposantes(hybride).length} composante(s)</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={'bg-white p-5 rounded-lg border border-gray-200 shadow-sm xl:col-span-8 ' + (paiementExistantHybride?.statut === 'cloture' ? 'opacity-50 pointer-events-none' : '')}>
              <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5">
                <div className="p-2 bg-[#246990] rounded-full mr-3">
                  <Calculator className="text-white" size={16} />
                </div>
                <div className="flex-1">
                  <h2 className="font-medium text-[#246990] text-lg">Calcul du salaire</h2>
                  <p className="text-sm text-gray-600">Salaire fixe, taux horaire et elements variables</p>
                </div>
              </div>

              {!selectedHybride ? (
                <div className="text-center py-12 text-gray-400">
                  <Calculator className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <h4 className="text-lg font-medium text-gray-700 mb-2">Aucun employe selectionne</h4>
                  <p className="text-sm">Selectionnez un employe hybride pour voir ses donnees</p>
                </div>
              ) : (() => {
                const hybride = selectedHybrideData;
                if (!hybride) {
                  return <div className="text-center py-12 text-gray-400"><AlertTriangle className="h-16 w-16 mx-auto mb-4 opacity-50" /><h4 className="text-lg font-medium text-gray-700 mb-2">Donnees non disponibles</h4></div>;
                }

                const calcul = calculerRemunerationHybride(hybride);
                const montantAffiche = paiementExistantHybride ? toNumber(paiementExistantHybride.montant_net) : calcul.salaire_net;
                const montantBrut = paiementExistantHybride ? toNumber(paiementExistantHybride.montant_brut) : calcul.salaire_base;
                const montantRetenues = paiementExistantHybride ? toNumber(paiementExistantHybride.montant_retenues) : calcul.total_retenues;
                const montantAvances = paiementExistantHybride ? toNumber(paiementExistantHybride.montant_avances) : calcul.total_avances;
                const fixedComposantes = getFixedComposantes(hybride);

                return (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-blue-900">{getHybrideName(hybride)}</div>
                          <div className="text-sm text-blue-700">Profil hybride - salaire fixe et taux horaire</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-blue-900">{formatMoney(calcul.salaire_base)}</div>
                          <div className="text-xs text-blue-700">Brut consolide</div>
                          <div className="mt-2 flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenSalaryRequestHybride(hybride)}
                              disabled={fixedComposantes.length === 0 || (fixedComposantes.length === 1 && !canModifyFixedComposante(fixedComposantes[0]))}
                              className="rounded-md border border-blue-200 bg-white px-2 py-1 text-xs font-semibold text-[#246990] hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Modifier
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenSalaryHistoryHybride(hybride)}
                              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                            >
                              Historique
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200"><div className="text-center"><div className="text-xl font-bold text-green-600">{calcul.nombre_composantes}</div><div className="text-xs text-green-700">Composantes</div></div></div>
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200"><div className="text-center"><div className="text-xl font-bold text-red-600">{formatMoney(calcul.total_retenues)}</div><div className="text-xs text-red-700">Retenues</div></div></div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200"><div className="text-center"><div className="text-xl font-bold text-gray-600">{calcul.total_heures > 0 ? calcul.total_heures.toLocaleString('fr-FR') + ' h' : calcul.jours_pointes + ' pts'}</div><div className="text-xs text-gray-700">Pointages</div></div></div>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200"><h4 className="text-sm font-medium text-gray-700">Detail des composantes de paie</h4></div>
                      {calcul.composantes.length === 0 ? (
                        <div className="p-6 text-center text-gray-400"><Clock className="w-12 h-12 mx-auto mb-2 opacity-50" /><p className="text-sm">Aucune composante de paie pour cette periode</p></div>
                      ) : (
                        <div className="overflow-x-auto max-h-96 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0"><tr className="border-b border-gray-200"><th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Circuit</th><th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Role</th><th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Pointages</th><th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Brut</th><th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Retenues</th><th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Avances</th><th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Reste</th><th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Actions</th></tr></thead>
                            <tbody className="divide-y divide-gray-200">
                              {calcul.composantes.map((composante, index) => {
                                const isEnseignant = composante.type_personnel === 'enseignant';
                                const isHourly = isHourlyComposante(composante);
                                const retenues = toNumber(composante.retenues_presence) + toNumber(composante.retenues_enfants) + toNumber(composante.retenue_manuelle);
                                const canModifySalary = canModifyFixedComposante(composante);
                                return (
                                  <tr key={composante.ligne_key || index} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 whitespace-nowrap text-xs"><span className={'inline-flex items-center gap-1 px-2 py-1 rounded-full ' + (isEnseignant ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-800')}>{isEnseignant ? <GraduationCap className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}{isEnseignant ? 'Enseignant' : 'Personnel'}</span></td>
                                    <td className="px-3 py-2 text-xs text-gray-700">{getComposanteLabel(composante)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{getPointageLabel(composante)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-right font-medium text-gray-900">{formatMoney(composante.salaire_brut)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-right text-red-600">- {formatMoney(retenues)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-right text-orange-600">- {formatMoney(composante.avances_total)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-right font-semibold text-[#246990]">{formatMoney(composante.reste_a_payer ?? composante.net_a_payer)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-right">
                                      <button
                                        type="button"
                                        onClick={() => handleOpenSalaryRequestHybride(hybride, composante)}
                                        disabled={!canModifySalary}
                                        className="rounded-md border border-slate-200 bg-white px-2 py-1 font-semibold text-[#246990] hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-white"
                                      >
                                        {isHourly ? 'Horaire' : canModifySalary ? 'Modifier' : 'A detailler'}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {paiementExistantHybride && paiementExistantHybride.statut === 'cloture' ? (
                      <div className="mb-4 p-4 bg-red-100 border-2 border-red-300 rounded-lg"><div className="flex items-center mb-2"><AlertTriangle className="w-6 h-6 text-red-600 mr-2" /><h4 className="font-bold text-red-900 text-lg">Paiement cloture pour cette periode</h4></div><div className="text-sm text-red-700 space-y-1"><div>Montant : {formatMoney(paiementExistantHybride.montant_net)}</div><div className="font-medium mt-2">Aucune modification n'est possible</div></div></div>
                    ) : paiementExistantHybride ? (
                      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg"><div className="flex items-center mb-2"><CheckCircle className="w-5 h-5 text-green-600 mr-2" /><h4 className="font-medium text-green-900">Paiement valide</h4></div><div className="text-sm text-green-700 space-y-1"><div>Montant : {formatMoney(paiementExistantHybride.montant_net)}</div></div></div>
                    ) : null}

                    <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                      <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center"><Calculator className="w-4 h-4 mr-2" />Detail du calcul</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600">Nombre de composantes :</span><span className="font-medium">{calcul.nombre_composantes}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Pointages consolides :</span><span className="font-medium">{calcul.total_heures > 0 ? calcul.total_heures.toLocaleString('fr-FR') + ' heures' : calcul.jours_pointes + ' pointage(s)'}</span></div>
                        {calcul.nombre_cours > 0 && <div className="flex justify-between text-green-600"><span>Cours pris en compte :</span><span className="font-medium">{calcul.nombre_cours}</span></div>}
                        {calcul.jours_manquants > 0 && <div className="flex justify-between text-orange-600"><span>Pointages manquants :</span><span className="font-medium">{calcul.jours_manquants}</span></div>}
                      </div>
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <div className="flex justify-between text-sm mb-2"><span className="text-gray-600">Salaire brut consolide :</span><span className="font-medium">{formatMoney(montantBrut)}</span></div>
                        {montantRetenues > 0 && (
                          <div className="space-y-1 mb-2">
                            {calcul.total_retenues_presences > 0 && (
                              <div className="flex justify-between text-sm text-red-600">
                                <span>Retenues absences/retards :</span>
                                <span className="font-medium">- {formatMoney(calcul.total_retenues_presences)}</span>
                              </div>
                            )}
                            {calcul.retenue_globale > 0 && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-sm text-red-600">
                                  <span>Retenue sur salaire :</span>
                                  <span className="font-medium">- {formatMoney(calcul.retenue_globale)}</span>
                                </div>
                                {calcul.justification_retenue_temp && (
                                  <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                    <span className="font-medium">Motif : </span>
                                    {calcul.justification_retenue_temp}
                                  </div>
                                )}
                              </div>
                            )}
                            {calcul.retenues_enfants_charge > 0 && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-sm text-purple-600">
                                  <span className="flex items-center gap-2"><Users className="w-3.5 h-3.5" />Retenues enfants a charge :</span>
                                  <span className="font-medium">- {formatMoney(calcul.retenues_enfants_charge)}</span>
                                </div>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded text-xs">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="text-gray-600">
                                      <span className="font-medium">{calcul.nombre_enfants_charge} enfant(s) total</span>
                                      <span> - Deduction automatique</span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedHybrideEnfantsDetail(hybride);
                                        setShowModalEnfantsCharge(true);
                                      }}
                                      className="text-purple-600 hover:text-purple-800 hover:underline text-xs font-medium flex items-center gap-1"
                                    >
                                      <Users className="w-3 h-3" />
                                      Voir detail
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="flex justify-between text-sm text-red-700 font-bold pt-1 border-t border-red-200">
                              <span>Total retenues :</span>
                              <span>- {formatMoney(montantRetenues)}</span>
                            </div>
                          </div>
                        )}
                        {montantAvances > 0 && <div className="flex justify-between text-sm text-orange-600 mb-2"><span>Avances sur salaire :</span><span className="font-medium">- {formatMoney(montantAvances)}</span></div>}
                        <div className="flex justify-between text-lg font-bold text-blue-900 pt-2 border-t border-blue-300"><span>Salaire net a payer :</span><span>{formatMoney(montantAffiche)}</span></div>

                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <button
                            type="button"
                            onClick={() => handleOpenRetenueHybride(hybride)}
                            disabled={paiementExistantHybride?.statut === 'cloture'}
                            className="w-full px-4 py-2.5 text-sm bg-[#c66d59] text-white rounded-lg hover:bg-[#b45d4c] transition-colors flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            <CreditCard className="w-4 h-4" />
                            Ajouter/Modifier une retenue sur salaire
                          </button>
                        </div>
                      </div>
                    </div>

                    {!calcul.peut_payer && !paymentValidatedHybride && <div className="p-4 bg-red-50 border border-red-200 rounded-lg"><div className="flex items-start"><AlertTriangle className="w-5 h-5 text-red-600 mr-2 mt-0.5" /><div className="flex-1"><h5 className="font-medium text-red-900 mb-1">Calcul hybride non payable</h5><p className="text-sm text-red-700 mb-2">{calcul.message_erreur}</p></div></div></div>}

                    {paymentValidatedHybride && <div className="p-3 bg-green-50 border border-green-200 rounded-lg"><div className="flex items-center text-sm text-green-800"><CheckCircle className="w-5 h-5 mr-2" /><div><p className="font-medium">Paiement deja effectue</p><p className="text-xs text-green-700">Ce salaire hybride a deja ete paye pour {new Date(selectedDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</p></div></div></div>}

                    <SalaryLockPanel />

                    <button
                      onClick={() => {
                        if (salaryPaymentLocked) {
                          localShowToast(getSalaryLockMessage(), 'error');
                          return;
                        }
                        const currentCalcul = calculerRemunerationHybride(hybride);
                        if (currentCalcul.peut_payer) setShowValidationDialogHybride(true);
                        else localShowToast(currentCalcul.message_erreur, 'error');
                      }}
                      disabled={calculationValidatedHybride || paymentValidatedHybride || salaryPaymentLocked}
                      className={'w-full py-3 px-4 rounded-lg font-medium transition-colors ' + (calculationValidatedHybride || paymentValidatedHybride || salaryPaymentLocked ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#246990] text-white hover:bg-[#1f5a7a]')}
                    >
                      {paymentValidatedHybride ? <div className="flex items-center justify-center"><CheckCircle className="w-5 h-5 mr-2" />Deja paye ce mois</div> : calculationValidatedHybride ? <div className="flex items-center justify-center"><Check className="w-5 h-5 mr-2" />Calcul valide</div> : <div className="flex items-center justify-center"><Brain className="w-5 h-5 mr-2" />Valider le calcul</div>}
                    </button>
                  </div>
                );
              })()}
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm xl:col-span-2">
              <div className="p-3 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-4">
                <div className="p-2 bg-[#246990] rounded-full mr-2"><CreditCard className="text-white" size={14} /></div>
                <div className="flex-1"><h2 className="font-medium text-[#246990] text-base">Paiement</h2><p className="text-xs text-gray-600">Validation</p></div>
              </div>
              <SalaryLockPanel />
              <div className={paiementExistantHybride?.statut === 'cloture' ? 'opacity-50 pointer-events-none' : ''}>
                {!calculationValidatedHybride ? (
                  <div className="text-center py-8 text-gray-400"><CreditCard className="h-12 w-12 mx-auto mb-2 opacity-50" /><p className="text-xs">Validez le calcul</p></div>
                ) : (() => {
                  const calcul = calculerRemunerationHybride(selectedHybrideData);
                  const montantAffiche = paiementExistantHybride ? toNumber(paiementExistantHybride.montant_net) : calcul.salaire_net;
                  const montantBrut = paiementExistantHybride ? toNumber(paiementExistantHybride.montant_brut) : calcul.salaire_base;
                  const montantRetenues = paiementExistantHybride ? toNumber(paiementExistantHybride.montant_retenues) : calcul.total_retenues;
                  const montantAvances = paiementExistantHybride ? toNumber(paiementExistantHybride.montant_avances) : calcul.total_avances;
                  return (
                    <div className="space-y-3">
                      <div className="text-center p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200"><div className="text-xs text-green-600 mb-1 font-medium">Remuneration calculee</div><div className="text-2xl font-bold text-green-700">{formatMoney(montantAffiche)}</div><div className="text-xs text-green-600 mt-2">Salaire hybride</div></div>
                      {!paymentValidatedHybride ? (
                        <div className="space-y-2"><button onClick={handleOpenPaymentDialogHybride} disabled={salaryPaymentLocked} className="w-full py-2 px-3 bg-[#319190] text-white rounded-lg hover:bg-[#319190]/90 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"><div className="flex items-center justify-center"><CreditCard className="w-4 h-4 mr-2" />Effectuer paiement</div></button></div>
                      ) : (
                        <div className="space-y-2"><div className="p-3 bg-green-600 text-white rounded-lg text-center"><div className="flex items-center justify-center mb-1"><CheckCircle className="w-5 h-5 mr-2" /><span className="font-medium text-sm">{paiementExistantHybride?.statut === 'cloture' ? 'Paiement cloture' : 'Paiement effectue'}</span></div><div className="text-xs opacity-90">{paiementExistantHybride?.date_paiement ? new Date(paiementExistantHybride.date_paiement).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR')}</div></div><div className="p-2 bg-gray-50 rounded-lg border border-gray-200 text-xs space-y-1"><div className="flex justify-between"><span className="text-gray-600">Montant:</span><span className="font-semibold text-gray-900">{formatMoney(montantAffiche)}</span></div><div className="flex justify-between"><span className="text-gray-600">Salaire brut:</span><span className="font-medium text-gray-900">{formatMoney(montantBrut)}</span></div>{montantRetenues > 0 && <div className="flex justify-between text-red-600"><span>Retenues:</span><span className="font-medium">-{formatMoney(montantRetenues)}</span></div>}{montantAvances > 0 && <div className="flex justify-between text-orange-600"><span>Avances:</span><span className="font-medium">-{formatMoney(montantAvances)}</span></div>}</div></div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showHistoriqueHybrides && (
        <EmployeePaimentHistory
          show={showHistoriqueHybrides}
          onClose={() => setShowHistoriqueHybrides(false)}
          loadData={async () => {
            try {
              const response = await api.paiementsSalaires.getHistoriquePaiementsHybrides();
              if (response.success) return response.data || [];
              localShowToast('Erreur lors du chargement de l historique', 'error');
              return [];
            } catch (error) {
              console.error('Erreur chargement historique hybrides:', error);
              localShowToast('Erreur lors du chargement de l historique', 'error');
              return [];
            }
          }}
          onDownloadFiche={handleDownloadFichePaieHybride}
          showToast={localShowToast}
          title="Historique des remunerations hybrides"
          subtitle="Employes hybrides - Registre comptable"
          personnelLabel="Employe hybride"
        />
      )}

      {showRetenueModal && createPortal(
        <div className={`${MODAL_OVERLAY_Z40_CLASS} flex items-center justify-center`}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Gerer les retenues sur salaire</h3>
              <button type="button" onClick={() => setShowRetenueModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-900">{selectedRetenueTarget?.nom}</p>
              <p className="text-xs text-blue-700">Periode : {formatDateForDisplay(selectedDate)}</p>
              <p className="mt-1 text-xs text-blue-700">La retenue sera repartie automatiquement sur les composantes fixe et horaire disponibles.</p>
            </div>

            <div className="mb-4">
              <div className="flex gap-3">
                <label className="flex-1 cursor-pointer">
                  <input type="radio" value="avec" checked={typeRetenue === 'avec'} onChange={(event) => setTypeRetenue(event.target.value)} className="sr-only" />
                  <div className={'p-3 rounded-lg border-2 text-center transition-colors ' + (typeRetenue === 'avec' ? 'border-red-600 bg-red-50' : 'border-gray-300 hover:border-gray-400')}>
                    <div className="text-sm font-medium">Appliquer une retenue</div>
                  </div>
                </label>
                <label className="flex-1 cursor-pointer">
                  <input type="radio" value="sans" checked={typeRetenue === 'sans'} onChange={(event) => setTypeRetenue(event.target.value)} className="sr-only" />
                  <div className={'p-3 rounded-lg border-2 text-center transition-colors ' + (typeRetenue === 'sans' ? 'border-green-600 bg-green-50' : 'border-gray-300 hover:border-gray-400')}>
                    <div className="text-sm font-medium">Pas de retenue</div>
                  </div>
                </label>
              </div>
            </div>

            {typeRetenue === 'avec' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Montant de la retenue (FCFA) *</label>
                  <input
                    type="number"
                    value={retenueAbsence}
                    onChange={(event) => setRetenueAbsence(event.target.value)}
                    placeholder="Ex: 5000"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Motif de la retenue *</label>
                  <textarea
                    value={justificationPasDeRetenue}
                    onChange={(event) => setJustificationPasDeRetenue(event.target.value)}
                    placeholder="Ex: absence injustifiee, retard repete, manquement professionnel..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                    rows="3"
                  />
                </div>
              </>
            )}

            {typeRetenue === 'sans' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Justification de l'absence de retenue *</label>
                <textarea
                  value={justificationPasDeRetenue}
                  onChange={(event) => setJustificationPasDeRetenue(event.target.value)}
                  placeholder="Ex: autorisation exceptionnelle, justificatif accepte..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                  rows="3"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button type="button" onClick={() => setShowRetenueModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Annuler</button>
              <button type="button" onClick={handleSaveRetenueHybride} className="flex-1 px-4 py-2 bg-[#246990] text-white rounded-lg hover:bg-[#1f5a7a]">Enregistrer</button>
            </div>
          </div>
        </div>
      , document.body)}

      {showModalEnfantsCharge && selectedHybrideEnfantsDetail && (() => {
        const calcul = calculerRemunerationHybride(selectedHybrideEnfantsDetail);
        return createPortal(
          <div className={`${MODAL_OVERLAY_Z40_CLASS} flex items-center justify-center p-4`}>
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-[#3084b5] to-[#319190] px-5 py-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg"><Users className="w-5 h-5" /></div>
                    <div>
                      <h3 className="text-lg font-semibold">Enfants a charge</h3>
                      <p className="text-white/90 text-xs">{getHybrideName(selectedHybrideEnfantsDetail)}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setShowModalEnfantsCharge(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div className={'grid ' + (calcul.retenues_enfants_intersite > 0 ? 'grid-cols-3' : 'grid-cols-2') + ' gap-3'}>
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <p className="text-xs text-blue-600 font-medium mb-1">Enfants</p>
                    <p className="text-2xl font-bold text-blue-900">{calcul.nombre_enfants_charge}</p>
                    {calcul.nombre_enfants_intersite > 0 && <p className="text-[10px] text-blue-500">{calcul.nombre_enfants_locaux} local + {calcul.nombre_enfants_intersite} inter-sites</p>}
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                    <p className="text-xs text-purple-600 font-medium mb-1">Retenue totale</p>
                    <p className="text-xl font-bold text-purple-900">{formatMoney(calcul.retenues_enfants_charge)}</p>
                  </div>
                  {calcul.retenues_enfants_intersite > 0 && (
                    <div className="bg-teal-50 rounded-lg p-3 border border-teal-100">
                      <p className="text-xs text-teal-600 font-medium mb-1">Inter-sites</p>
                      <p className="text-xl font-bold text-teal-900">{formatMoney(calcul.retenues_enfants_intersite)}</p>
                    </div>
                  )}
                </div>

                {calcul.detail_enfants_charge.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Enfants scolarises sur ce site</h4>
                    <div className="space-y-2">
                      {calcul.detail_enfants_charge.map((enfant, index) => (
                        <div key={enfant.eleve_id || index} className="bg-white rounded-lg border border-gray-200 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{enfant.prenom} {enfant.nom}</p>
                              <p className="text-xs text-gray-500">{enfant.matricule || 'Matricule non renseigne'}</p>
                            </div>
                            <div className="text-right text-sm font-bold text-[#3084b5]">{formatMoney(enfant.montant)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {calcul.detail_enfants_intersite.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-2">Enfants scolarises sur d'autres sites</h4>
                    <div className="space-y-2">
                      {calcul.detail_enfants_intersite.map((enfant, index) => (
                        <div key={enfant.link_id || enfant.eleve_id || index} className="bg-white rounded-lg border border-teal-200 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{enfant.nom_complet || enfant.nom}</p>
                              <p className="text-xs text-teal-600">{enfant.site_label || enfant.site_subdomain || 'Site distant'}</p>
                            </div>
                            <div className="text-right text-sm font-bold text-teal-600">{formatMoney(enfant.montant || enfant.montant_retenue_mensuel)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {calcul.detail_repartition_enfants.length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Repartition sur les composantes</h4>
                    <div className="space-y-1">
                      {calcul.detail_repartition_enfants.map((item) => (
                        <div key={item.ligne_key} className="flex justify-between gap-3 text-xs text-slate-700">
                          <span className="truncate">{item.role}</span>
                          <span className="font-semibold">{formatMoney(item.local + item.intersite)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                <p className="text-xs text-gray-500">{formatDateForDisplay(selectedDate)}</p>
                <button type="button" onClick={() => setShowModalEnfantsCharge(false)} className="px-4 py-1.5 bg-[#3084b5] text-white rounded-md hover:bg-[#2a6d9a] transition-colors text-sm font-medium">Fermer</button>
              </div>
            </div>
          </div>
        , document.body);
      })()}

      <SalaryModificationRequestModal
        open={!!salaryRequestEmploye}
        employee={salaryRequestEmploye}
        selectedDate={selectedDate}
        anneeActive={anneeActive}
        typePersonnel="hybride"
        onClose={() => setSalaryRequestEmploye(null)}
        onSuccess={loadHybridesData}
        showToast={localShowToast}
      />

      <SalaryModificationHistoryModal
        open={!!salaryHistoryEmploye}
        employee={salaryHistoryEmploye}
        selectedDate={selectedDate}
        anneeActive={anneeActive}
        onClose={() => setSalaryHistoryEmploye(null)}
        showToast={localShowToast}
      />

      <ConfirmDialog isOpen={showValidationDialogHybride} onClose={() => setShowValidationDialogHybride(false)} onConfirm={handleValidateCalculationHybride} title="Valider le calcul" description="Voulez-vous valider ce calcul de remuneration hybride et l'envoyer vers le module de paiement ?" confirmText="Valider" cancelText="Annuler" type="question" />

      {isValidatingHybride && createPortal(
        <div className={`${MODAL_OVERLAY_Z50_CLASS} flex items-center justify-center`}><div className="bg-white rounded-lg p-8 max-w-md w-full mx-4"><div className="text-center"><div className="mb-6"><div className="w-16 h-16 mx-auto bg-[#246990] rounded-full flex items-center justify-center animate-pulse"><Calculator className="text-white" size={32} /></div></div><h3 className="text-xl font-semibold text-gray-900 mb-2">Validation en cours</h3><p className="text-gray-600 mb-6">Calcul hybride en cours de validation et envoi vers le paiement...</p><motion.div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><motion.div className="h-full bg-[#246990]" initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: 3, ease: 'easeInOut' }} /></motion.div></div></div></div>
      , document.body)}

      <ConfirmDialog
        isOpen={showPaymentDialogHybride && !isProcessingPaymentHybride}
        onClose={() => setShowPaymentDialogHybride(false)}
        onConfirm={handleProcessPaymentHybride}
        title="Effectuer le paiement"
        description={
          <div className="space-y-3">
            <p>Confirmez le paiement du salaire pour cet employe hybride.</p>
            <div className="bg-blue-50 p-3 rounded-lg"><div className="text-sm font-medium text-blue-900">Montant : {formatMoney(calculerRemunerationHybride(selectedHybrideData).salaire_net)}</div></div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mode de paiement *</label>
              <div className="relative mode-paiement-dropdown">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                <input
                  type="text"
                  placeholder="Rechercher un mode de paiement..."
                  value={selectedModePaiementHybride ? (modesPaiement.find((item) => String(item.id) === String(selectedModePaiementHybride))?.nom || '') : modePaiementSearchTerm}
                  onChange={(event) => {
                    setModePaiementSearchTerm(event.target.value);
                    setIsModePaiementDropdownOpen(true);
                    if (selectedModePaiementHybride) setSelectedModePaiementHybride('');
                  }}
                  onFocus={() => setIsModePaiementDropdownOpen(true)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3084b5] focus:border-[#3084b5] transition-all"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"><svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg></div>
                {isModePaiementDropdownOpen && !selectedModePaiementHybride && (
                  <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {modesPaiement.filter((mode) => normalizeText(mode.nom).includes(normalizeText(modePaiementSearchTerm))).length === 0 ? <div className="p-4 text-center text-gray-500 text-sm">Aucun mode de paiement trouve</div> : modesPaiement.filter((mode) => normalizeText(mode.nom).includes(normalizeText(modePaiementSearchTerm))).map((mode) => (
                      <div key={mode.id} onClick={() => { setSelectedModePaiementHybride(String(mode.id)); setModePaiementSearchTerm(''); setIsModePaiementDropdownOpen(false); }} className="px-4 py-3 hover:bg-[#3084b5]/10 cursor-pointer border-b last:border-b-0 transition-colors group"><div className="flex items-center gap-3"><div className="flex-shrink-0 w-8 h-8 bg-[#3084b5]/10 rounded-lg flex items-center justify-center group-hover:bg-[#3084b5] transition-colors"><CreditCard className="w-4 h-4 text-[#3084b5] group-hover:text-white transition-colors" /></div><div className="flex-1 min-w-0"><div className="font-medium text-gray-900 text-sm group-hover:text-[#3084b5] transition-colors">{mode.nom}</div></div></div></div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        }
        confirmText="Confirmer le paiement"
        cancelText="Annuler"
        type="question"
      />

      {isProcessingPaymentHybride && (() => {
        const hybride = selectedHybrideData;
        const calcul = calculerRemunerationHybride(hybride);
        const isClotureStep = paymentStep === 'cloture';
        return createPortal(
          <div className={`${MODAL_OVERLAY_Z50_CLASS} flex items-center justify-center`}><div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4"><div className="text-center mb-6"><div className={'w-20 h-20 mx-auto rounded-full flex items-center justify-center animate-pulse mb-4 shadow-lg ' + (isClotureStep ? 'bg-gradient-to-br from-[#319190] to-[#3084b5]' : 'bg-gradient-to-br from-[#3084b5] to-[#319190]')}>{isClotureStep ? <Check className="text-white" size={36} /> : <CreditCard className="text-white" size={36} />}</div><h3 className="text-2xl font-bold text-gray-900 mb-2">{isClotureStep ? 'Cloture du paiement' : 'Traitement du paiement'}</h3><p className="text-gray-600">{isClotureStep ? 'Finalisation en cours...' : 'Enregistrement en cours pour ' + getHybrideName(hybride)}</p><div className="flex items-center justify-center gap-2 mt-4"><div className={'flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ' + (isClotureStep ? 'bg-[#319190] text-white' : 'bg-[#3084b5] text-white')}>{isClotureStep ? '✓' : '1'}</div><div className={'h-1 w-12 ' + (isClotureStep ? 'bg-[#319190]' : 'bg-gray-300')}></div><div className={'flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ' + (isClotureStep ? 'bg-[#319190] text-white' : 'bg-gray-300 text-gray-600')}>2</div></div></div>{!isClotureStep ? <div className="space-y-4 mb-6"><div className="p-4 bg-[#3084b5]/10 rounded-lg border-2 border-[#3084b5]/30"><div className="flex items-center justify-between"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-[#3084b5] rounded-lg flex items-center justify-center shadow-md"><Banknote className="text-white" size={20} /></div><div><p className="text-sm text-[#3084b5] font-semibold">Salaire hybride</p><p className="text-xs text-[#3084b5]/70">{formatDateForDisplay(selectedDate)}</p></div></div><div className="text-right"><p className="text-lg font-bold text-[#3084b5]">{formatMoney(calcul.salaire_net)}</p><p className="text-xs text-[#3084b5]/70">Net a payer</p></div></div></div><div className="p-4 bg-slate-50 rounded-lg border-2 border-slate-200"><div className="flex items-start justify-between mb-3"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center shadow-md"><Layers className="text-white" size={20} /></div><div><p className="text-sm text-slate-700 font-semibold">Composantes de paie</p><p className="text-xs text-slate-600">{calcul.nombre_composantes} composante(s) consolidee(s)</p></div></div><div className="text-right"><p className="text-lg font-bold text-slate-700">{formatMoney(calcul.salaire_base)}</p><p className="text-xs text-slate-600">Brut consolide</p></div></div><div className="space-y-1.5 mt-3 pt-3 border-t border-slate-200">{calcul.composantes.map((composante, index) => <div key={composante.ligne_key || index} className="flex items-center justify-between text-xs"><span className="text-slate-700"><BookOpen className="w-3 h-3 inline mr-1" />{getComposanteLabel(composante)}</span><span className="font-medium text-slate-800">{formatMoney(composante.reste_a_payer ?? composante.net_a_payer)}</span></div>)}</div></div></div> : <div className="space-y-4 mb-6"><div className="p-6 bg-[#319190]/10 rounded-lg border-2 border-[#319190]/40 text-center"><CheckCircle className="w-16 h-16 text-[#319190] mx-auto mb-4" /><h4 className="text-lg font-bold text-gray-900 mb-2">Finalisation du paiement</h4><p className="text-sm text-gray-700">Le paiement hybride est en cours de cloture definitive.<br />Cette operation est irreversible.</p></div></div>}<div className="space-y-2"><div className="flex items-center justify-between text-sm text-gray-600"><span className="font-medium">{isClotureStep ? 'Etape 2/2 : Cloture...' : 'Etape 1/2 : Paiement...'}</span><span className="flex items-center gap-1 text-[#3084b5] font-semibold"><Clock className="w-4 h-4" />{isClotureStep ? '5 secondes' : '8 secondes'}</span></div><motion.div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><motion.div className={'h-full shadow-lg ' + (isClotureStep ? 'bg-gradient-to-r from-[#319190] to-[#3084b5]' : 'bg-gradient-to-r from-[#3084b5] to-[#319190]')} key={paymentStep} initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: isClotureStep ? 5 : 8, ease: 'easeInOut' }} /></motion.div></div></div></div>
        , document.body);
      })()}

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

export default ComptabiliteHybrides;
